// ----------------------------------------------------------------------

function pxToRem(value) {
  return `${value / 16}rem`;
}

function responsiveFontSizes({ sm, md, lg }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm),
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md),
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg),
    },
  };
}

// ----------------------------------------------------------------------
// Three families, one job each.
//
//   Plus Jakarta Sans — headings. Geometric with a tall x-height and tight,
//     closed apertures, which holds up at 64px without going soft.
//   Inter            — body. Built for screens at 14-16px: tall x-height, open
//     counters, unambiguous 1/l/I. The one that gets read the most.
//   Manrope          — accent / UI. Buttons, eyebrows, labels, chips. Its
//     slightly rounded, wide forms read as "interface" rather than "prose",
//     which separates controls from content at a glance.
//
// Fallbacks are the platform UI stack. On Windows that resolves to Segoe UI
// rather than Times, so a failed font request degrades to a sans-serif rather
// than a serif.

const FONT_HEADING = "'Plus Jakarta Sans', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const FONT_BODY = "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const FONT_ACCENT = "'Manrope', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

// ----------------------------------------------------------------------

const typography = {
  fontFamily: FONT_BODY,

  // MUI reads these keys for component defaults (Chip, Alert, etc). The old
  // values (100/300/400) put "bold" at regular weight, so every component that
  // asked for bold got body text.
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,

  // Display type needs negative tracking or it looks loose; the larger the cut,
  // the tighter it can go before the counters start to close up.
  h1: {
    fontFamily: FONT_HEADING,
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: '-0.03em',
    fontSize: pxToRem(36),
    ...responsiveFontSizes({ sm: 46, md: 54, lg: 60 }),
  },
  h2: {
    fontFamily: FONT_HEADING,
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: '-0.025em',
    fontSize: pxToRem(30),
    ...responsiveFontSizes({ sm: 36, md: 40, lg: 44 }),
  },
  h3: {
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    lineHeight: 1.25,
    letterSpacing: '-0.02em',
    fontSize: pxToRem(24),
    ...responsiveFontSizes({ sm: 26, md: 28, lg: 30 }),
  },
  h4: {
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '-0.015em',
    fontSize: pxToRem(20),
    ...responsiveFontSizes({ sm: 20, md: 22, lg: 24 }),
  },
  h5: {
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    lineHeight: 1.35,
    letterSpacing: '-0.01em',
    fontSize: pxToRem(18),
  },
  h6: {
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    lineHeight: 1.4,
    fontSize: pxToRem(17),
  },

  // Lead-in under a heading, so it belongs to the heading's voice. Rendered
  // inside heading blocks it has to match them or the pairing looks accidental.
  subtitle1: {
    fontFamily: FONT_HEADING,
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '-0.01em',
    fontSize: pxToRem(17),
  },
  // Inline emphasis inside a paragraph — body family, heavier cut.
  subtitle2: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(14),
  },

  // 1.65 rather than the usual 1.5: long-form paragraphs on this site (course
  // descriptions, blog bodies, licence explainers) are the main content, and
  // they need the extra leading to stay comfortable.
  body1: {
    lineHeight: 1.65,
    fontSize: pxToRem(16),
  },
  body2: {
    lineHeight: 1.6,
    fontSize: pxToRem(14),
  },
  caption: {
    lineHeight: 1.5,
    letterSpacing: '0.01em',
    fontSize: pxToRem(12),
  },
  // Uppercase small caps need positive tracking; at 0 it reads as a solid block.
  overline: {
    fontFamily: FONT_ACCENT,
    fontWeight: 700,
    lineHeight: 1.5,
    letterSpacing: '0.08em',
    fontSize: pxToRem(12),
    textTransform: 'uppercase',
  },
  button: {
    fontFamily: FONT_ACCENT,
    fontWeight: 700,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
    fontSize: pxToRem(14),
  },
};

export default typography;